import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import debounce from 'lodash/debounce';
import Tooltip from '@material-ui/core/Tooltip';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import { makeStyles } from '@material-ui/core';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './Channel.module.css';
import * as utils from '../../../../helpers/utils';
import { getChannelIcon } from '../../../../helpers/channelHelpers';
import { getContactNameForChannels } from '../../../../helpers/contactHelpers';

const useStyles = makeStyles(() => ({
  hideButton: {
    position: 'absolute',
    top: '13px',
    right: '14px',
    padding: '0',
    margin: '0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
}));

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const Channel = observer(function Channel(props) {
  const classes = useStyles();
  const { commonStore, messageStore, settingsStore, channelStore, sendingEvents } = useRootStore();
  const { t } = useTranslation();
  const { channel } = props;

  const settings = settingsStore.getLoggedUserSettings;
  const numOfUnseenMessages = messageStore.computedGetUnseenMessages(channel, null, null).length;
  const selectedChannelName = commonStore.computedGetChannelName(channel);
  const latestMessage = messageStore.computedGetLastMessageForChannel(channel);
  const messageTimeStamp = messageStore.computedGetFormattedTimestamp(latestMessage);
  let latestMessageSender = null;

  const openChannel = debounce(() => {
    // if (settings?.SelectedChannel?._id !== channel._id) {
    commonStore.setChannelSearchInput('');
    commonStore.setToggleDisplayEditCustomerNamesForm(false);
    commonStore.setChatInputStyleHeight(0);
    commonStore.setFullModeChatFileUpload(null);

    sendingEvents.socketEmitRoom(
      channel,
      utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
    );
    // }
  }, 250);

  if (latestMessage?.Sender) {
    if (latestMessage.Sender._id === commonStore.getLoggedUser._id) {
      latestMessageSender = t('Channel.MessageSenderYou');
    } else {
      latestMessageSender = latestMessage.Sender.getFullName
        ? `${getContactNameForChannels(latestMessage.Sender)}: `
        : null;
    }
  }

  const channelToolTip = (
    <div className={styles.tooltipContainer}>
      {channel.ChannelStateType?.TypeName ? (
        <span>{`${t('Channel.WebChatState')}: ${channel.ChannelStateType.TypeName}`}</span>
      ) : null}

      <span>{selectedChannelName}</span>

      {channel.CustomerPhoneNumber && channel.CustomerPhoneNumber.Number ? (
        <React.Fragment>
          {channel.CustomerPhoneNumber.Description ? (
            <span className={styles.tooltipPhoneDescription}>
              {channel.CustomerPhoneNumber.Description}
            </span>
          ) : null}

          <span>{channel.CustomerPhoneNumber.Number}</span>
        </React.Fragment>
      ) : null}

      {latestMessage && messageTimeStamp ? (
        <React.Fragment>
          <span className={styles.tooltipLastMessage}>
            {latestMessageSender} {latestMessage.MessageText}
          </span>

          <span>{messageTimeStamp}</span>
        </React.Fragment>
      ) : null}
    </div>
  );

  const hideConversation = debounce(() => {
    sendingEvents.socketEmitHideChannel(channel._id);

    if (settings?.SelectedChannel?._id === channel._id) {
      const [newSelectedChannel] = channelStore.getChannels.filter((f) => f._id !== channel._id);
      sendingEvents.socketEmitRoom(
        newSelectedChannel,
        utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
      );
    }
  }, 250);

  const isChannelWebChat =
    channel?.ChannelType && channel.ChannelType.TypeId === utils.ChannelTypeEnum.WebChat.TypeId;

  const isChannelWebChatClosed =
    isChannelWebChat &&
    channel?.ChannelStateType?.TypeId === utils.ChannelStateTypeEnum.Closed.TypeId;

  const channelIcon = getChannelIcon(channel);

  const channelContainer = [styles.channelContainer];
  const hideButtonContainerStyles = [styles.hideButtonContainer];

  if (!isChannelWebChat || isChannelWebChatClosed) {
    channelContainer.push(styles.hoverMargin);
  }

  if (settings.SelectedChannel && settings.SelectedChannel._id === channel._id) {
    channelContainer.push(styles.selected);
    hideButtonContainerStyles.push(styles.selected);
  }

  if (isChannelWebChat) {
    channelContainer.push(styles.blueBackground);
  }

  return (
    <Grid component="div" container>
      <Grid
        component="div"
        container
        item
        xs={12}
        justify="flex-start"
        alignItems="flex-start"
        alignContent="center"
        className={channelContainer.join(' ')}
      >
        <Grid component="div" container item xs>
          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={channelToolTip}
          >
            <button
              type="button"
              onClick={openChannel}
              className={styles.buttonContainer}
              data-testid="channel"
              data-testchannelname={`Channel=${selectedChannelName}`}
            >
              <Grid component="div" container>
                <Grid
                  component="div"
                  container
                  item
                  xs
                  className={styles.channelIconContainer}
                  justify="flex-start"
                  alignItems="center"
                  alignContent="center"
                >
                  <img src={channelIcon} alt="Channel logo" className={styles.channelIcon} />
                </Grid>

                <Grid
                  component="div"
                  container
                  item
                  xs
                  justify="flex-start"
                  alignItems="center"
                  alignContent="center"
                  className={styles.channelTextContainer}
                >
                  <Grid component="div" container item xs={12} alignItems="center">
                    <span
                      className={
                        !numOfUnseenMessages
                          ? styles.channelNameStyle
                          : [styles.channelNameStyle, styles.bold].join(' ')
                      }
                    >
                      {selectedChannelName}
                    </span>

                    {latestMessage ? (
                      <span
                        className={styles.lastMessageTimeStamp}
                        data-testid="lastMessageTimeStamp"
                      >
                        {messageTimeStamp}
                      </span>
                    ) : null}
                  </Grid>

                  {latestMessage ? (
                    <Grid component="div" container item xs={12}>
                      <span
                        className={
                          !numOfUnseenMessages
                            ? styles.lastMessage
                            : [styles.lastMessage, styles.bold].join(' ')
                        }
                        data-testid="lastMessage"
                      >
                        {latestMessageSender}

                        {latestMessage.MessageText || t('Channel.UploadedFile')}
                      </span>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </button>
          </Tooltip>
        </Grid>

        {!isChannelWebChat || isChannelWebChatClosed ? (
          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={t('Common.HideConversation')}
            className={hideButtonContainerStyles.join(' ')}
          >
            <IconButton className={classes.hideButton} size="small" onClick={hideConversation}>
              <CancelIcon
                style={{
                  borderRadius: '50%',
                  fontSize: '20px',
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </Grid>
    </Grid>
  );
});
Channel.whyDidYouRender = true;
Channel.displayName = 'Channel';

export default Channel;
